import { CommonModule } from '@angular/common';
import { Component, Inject, inject, Injector } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Row } from '@vendasta/galaxy/table';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import {
  Association,
  CrmAssociationService,
  SelectedObjects,
  CrmObjectDisplayService,
  CrmObjectService,
  CrmFieldService,
  StandardIds,
  combineAssociationsWithChanges,
} from '../../../shared-services';
import { TranslationModule } from '../../../i18n';
import { AssociationCardComponent } from '../association-card';
import { AssociationsDialogData } from '../select-associations-modal/select-associations-modal.component';
import {
  ListObjectTypeDialogData,
  SelectObjectTypeModalComponent,
} from '../../select-object-type-modal/select-object-type-modal.component';
import { MatCardModule } from '@angular/material/card';
import { CrmObjectInterface } from '@vendasta/crm/lib/_internal/interfaces/crm-objects.interface';
import {
  CardData,
  COMPANY_PROFILE_CARD_DATA_TOKEN,
  CrmDependencies,
  CrmInjectionToken,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
  tableFiltersInjectionTokenGenerator,
} from '../../../tokens-and-interfaces';
import { PAGE_ROUTES, SNACKBAR_DURATION } from '../../../constants';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';

interface ChildCompanyAssociationsCardData extends CardData {
  associationId: string;
  parentCompanyName: string;
}

interface ParentCompanyAssociationsCardData extends CardData {
  associationId: string;
  childCompanyName: string;
}

@Component({
  selector: 'crm-parent-child-company-associations-card',
  imports: [
    CommonModule,
    TranslationModule,
    MatExpansionModule,
    AssociationCardComponent,
    GalaxyAlertModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    MatCardModule,
  ],
  providers: [
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
    },
  ],
  templateUrl: './parent-child-company-associations-card.component.html',
  styleUrls: [
    './parent-child-company-associations-card.component.scss',
    '../../../shared-styles/association-panel-shared.scss',
  ],
})
export class ParentChildCompanyAssociationsCardComponent {
  data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  childCompanies$: Observable<ChildCompanyAssociationsCardData[]>;
  parentCompany$: Observable<ParentCompanyAssociationsCardData>;

  getAssociations$: Observable<Association[]>;
  loadAssociations$$ = new BehaviorSubject<Association[]>([]);
  preSelectedRows$$ = new BehaviorSubject<Association[]>([]);
  fullName$: Observable<string>;
  modalData$: Observable<AssociationsDialogData>;
  canOpenModal = false;
  removedAssociationIds$$ = new BehaviorSubject<string[]>([]);
  removedParentAssociationIds$$ = new BehaviorSubject<string[]>([]);
  setParentAssociation$$ = new BehaviorSubject<Association | undefined>(undefined);

  newCompany$$ = new BehaviorSubject<string>('');
  changedAssociations$$ = new BehaviorSubject<Association[]>([]);
  private readonly confirmationModalService = inject(OpenConfirmationModalService);

  private readonly injector = inject(Injector);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectService: CrmObjectService,
    private readonly fieldService: CrmFieldService,
    private readonly associationService: CrmAssociationService,
    private readonly dialog: MatDialog,
    private translateService: TranslateService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private crmAssociationService: CrmAssociationService,
    private readonly snackService: SnackbarService,
  ) {
    this.associationService.setCrmObjectIdToAssociate(this.data?.company_id || '');
    this.fullName$ = objectService.getMultiObject('Company', [this.data.company_id || '']).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return '';
        }
        return this.objectDisplayService.getObjectDisplayName('Company', object || {});
      }),
    );

    this.getAssociations$ = this.associationService.getChildCompanyAssociationsForCompany$(this.data.company_id || '');

    const latestCompanies$ = this.loadAssociations$$.pipe(
      switchMap(() =>
        combineLatest([
          this.getAssociations$,
          this.config.routePrefix$,
          this.loadAssociations$$,
          this.changedAssociations$$,
          this.fullName$,
        ]),
      ),
      tap(() => {
        this.removedAssociationIds$$.next([]);
      }),
      map(([associations, routePrefix, copyCrmObject, changed, fullName]) => {
        const associatedObjects = copyCrmObject.map((association) => {
          return {
            associationId: association.associationId,
            associationType: 'Company-Company',
            tags: [],
            associatedObject: association,
          } as Association;
        });

        const combinedData = [...associations, ...associatedObjects];
        const existingAssociationsCombined = [...associatedObjects];
        this.preSelectedRows$$.next(existingAssociationsCombined);
        const dataWithChanges = combineAssociationsWithChanges(combinedData, changed);

        return dataWithChanges
          .filter((association, index, array) => {
            return (
              !!association.associatedObject &&
              array.findIndex((a) => a.associationId === association.associationId) === index
            );
          })
          .map((association) => {
            if (!association.associatedObject) {
              return {} as ChildCompanyAssociationsCardData;
            }

            const data = {
              associationId: association.associationId,
              parentCompanyName: fullName,
              businessName: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.CompanyName,
              )?.stringValue,
              businessWebsite: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.CompanyWebsite,
              )?.stringValue,
              type: 'business',
              tags: (association.tags || []).filter((tag) => tag !== 'Parent'),
              id: association.associatedObject.crmObjectId || '',
              profileButtonUrl:
                `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
                  ':crmObjectId',
                  association.associatedObject.crmObjectId || '',
                ),
            } as ChildCompanyAssociationsCardData;
            return data;
          });
      }),
    );

    this.childCompanies$ = combineLatest([latestCompanies$, this.removedAssociationIds$$.asObservable()]).pipe(
      map(([companies, removed]) => {
        return companies.filter((company) => {
          return !removed.includes(company.associationId);
        });
      }),
    );

    const latestParentCompany$ = this.associationService.getParentCompanyAssociationsForCompany$(
      this.data.company_id || '',
    );
    this.parentCompany$ = combineLatest([
      latestParentCompany$,
      this.removedParentAssociationIds$$.asObservable(),
      this.config.routePrefix$,
      this.fullName$,
      this.setParentAssociation$$.asObservable(),
    ]).pipe(
      map(([parentCompany, removed, routePrefix, fullName, setParentAssociation]) => {
        if (setParentAssociation) {
          parentCompany.splice(0, 0, {
            associationId: setParentAssociation.associationId,
            associationType: 'Company-Company',
            tags: [],
            associatedObject: setParentAssociation,
          } as Association);
        }
        return parentCompany
          .filter((company) => {
            return !removed.includes(company.associationId);
          })
          .map((company) => {
            return {
              associationId: company.associationId,
              childCompanyName: fullName,
              businessName: this.fieldService.getFieldValueFromCrmObject(
                company.associatedObject,
                StandardIds.CompanyName,
              )?.stringValue,
              businessWebsite: this.fieldService.getFieldValueFromCrmObject(
                company.associatedObject,
                StandardIds.CompanyWebsite,
              )?.stringValue,
              type: 'business',
              tags: (company.tags || []).filter((tag) => tag !== 'Parent'),
              id: company.associatedObject.crmObjectId || '',
              profileButtonUrl:
                `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
                  ':crmObjectId',
                  company.associatedObject.crmObjectId || '',
                ),
            } as ParentCompanyAssociationsCardData;
          })[0];
      }),
    );

    this.modalData$ = combineLatest([this.fullName$, this.preSelectedRows$$, this.newCompany$$]).pipe(
      map(([objectIdAndName, associations, newContact]) => ({
        modalTitle: objectIdAndName,
        primarySelect: '',
        description: '',
        selectedRows: [],
        selectedCompanyNames: [],
        fromId: '',
        copyCrmObjects: [],
        associationType: '',
        associations: associations,
        newSelection: newContact,
      })),
    );
  }

  openAddChildModal(): void {
    if (this.canOpenModal) {
      return;
    }
    this.canOpenModal = true;
    this.modalData$
      .pipe(
        take(1),
        tap(() => {
          this.canOpenModal = false;
        }),
        switchMap((data) => {
          const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
            data: {
              baseColumnIds: [],
              objectType: 'Company',
              modalTitle: this.translateService.instant(
                'ASSOCIATIONS.PARENT_CHILD_COMPANIES.ADD_CHILD_COMPANY_MODAL.TITLE',
                {
                  companyName: data.modalTitle,
                },
              ),
              associations: data.associations,
              associationType: 'Company-Company',
              newSelection: data.newSelection,
              hideCreateButton: true,
            } as ListObjectTypeDialogData,
            injector: Injector.create({
              providers: [
                {
                  provide: PageAnalyticsInjectionToken,
                  useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
                },
                {
                  provide: GalaxyFilterChipInjectionToken,
                  useFactory: tableFiltersInjectionTokenGenerator('Company', undefined, false),
                },
              ],
              parent: this.injector,
            }),
          });
          return dialogRef.afterClosed();
        }),
        map((modalResponse) => {
          let selectedRows = modalResponse?.selectedRows || [];
          if (modalResponse?.clickedCreate === true) {
            selectedRows = new Array<Row>();
          }
          return selectedRows;
        }),
        filter((selectedRows) => !!selectedRows),
        switchMap((selectedRows: Row[]) => {
          const selectedIDs = selectedRows.map((row) => {
            return row.id;
          });
          return this.objectService.getMultiObject('Company', selectedIDs).pipe(
            map((resp) => {
              const copyCrmObjects: CrmObjectInterface[] = resp.crmObjects?.map((object) => object) || [];
              return {
                crmObjects: copyCrmObjects,
                objects: resp.crmObjects?.map((object) => {
                  return {
                    id: object?.crmObjectId,
                    name: this.objectDisplayService.getObjectDisplayName('Company', object || {}),
                  };
                }),
              } as SelectedObjects;
            }),
          );
        }),
        tap((selectedObjects: SelectedObjects) => {
          const updatedObjects = {
            crmObjects: selectedObjects.crmObjects || [],
            objects: selectedObjects.objects || [],
          };
          this.associationService.setCopyCrmObjects(updatedObjects);
        }),
        mergeMap((): Observable<any> => {
          return this.crmAssociationService.createAssociation$('Company-Company', ['Parent'], false);
        }),
      )

      .subscribe((copyCrmObjects) => {
        if (copyCrmObjects.respCount.error) {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
            duration: SNACKBAR_DURATION,
            interpolateTranslateParams: {
              createdCount: copyCrmObjects.respCount.createdCount,
              totalCount: copyCrmObjects.respCount.totalCount,
            },
          });
        } else {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.GENERIC', {
            duration: SNACKBAR_DURATION,
          });
        }
        this.loadAssociations$$.next(copyCrmObjects.copyCrmObjectsUpdated);
      });
  }

  openSetParentModal(): void {
    if (this.canOpenModal) {
      return;
    }
    this.canOpenModal = true;
    this.modalData$
      .pipe(
        take(1),
        tap(() => {
          this.canOpenModal = false;
        }),
        switchMap((data) => {
          const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
            data: {
              baseColumnIds: [],
              objectType: 'Company',
              modalTitle: this.translateService.instant(
                'ASSOCIATIONS.PARENT_CHILD_COMPANIES.SET_PARENT_COMPANY_MODAL.TITLE',
                {
                  companyName: data.modalTitle,
                },
              ),
              associations: data.associations,
              associationType: 'Company-Company',
              newSelection: data.newSelection,
              hideCreateButton: true,
              singleSelection: true,
            } as ListObjectTypeDialogData,
            injector: Injector.create({
              providers: [
                {
                  provide: PageAnalyticsInjectionToken,
                  useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
                },
                {
                  provide: GalaxyFilterChipInjectionToken,
                  useFactory: tableFiltersInjectionTokenGenerator('Company', undefined, false),
                },
              ],
              parent: this.injector,
            }),
          });
          return dialogRef.afterClosed();
        }),
        map((modalResponse) => {
          let selectedRows = modalResponse?.selectedRows || [];
          if (modalResponse?.clickedCreate === true) {
            selectedRows = new Array<Row>();
          }
          return selectedRows;
        }),
        filter((selectedRows) => !!selectedRows),
        switchMap((selectedRows: Row[]) => {
          const selectedIDs = selectedRows.map((row) => {
            return row.id;
          });
          return this.objectService.getMultiObject('Company', selectedIDs).pipe(
            map((resp) => {
              const copyCrmObjects: CrmObjectInterface[] = resp.crmObjects?.map((object) => object) || [];
              return {
                crmObjects: copyCrmObjects,
                objects: resp.crmObjects?.map((object) => {
                  return {
                    id: object?.crmObjectId,
                    name: this.objectDisplayService.getObjectDisplayName('Company', object || {}),
                  };
                }),
              } as SelectedObjects;
            }),
          );
        }),
        tap((selectedObjects: SelectedObjects) => {
          const updatedObjects = {
            crmObjects: selectedObjects.crmObjects || [],
            objects: selectedObjects.objects || [],
          };
          this.associationService.setCopyCrmObjects(updatedObjects);
        }),
        mergeMap((): Observable<any> => {
          return this.crmAssociationService.createAssociation$('Company-Company', ['Parent'], false, 'to');
        }),
      )

      .subscribe((copyCrmObjects) => {
        if (copyCrmObjects.respCount.error) {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
            duration: SNACKBAR_DURATION,
            interpolateTranslateParams: {
              createdCount: copyCrmObjects.respCount.createdCount,
              totalCount: copyCrmObjects.respCount.totalCount,
            },
          });
        } else {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.GENERIC', {
            duration: SNACKBAR_DURATION,
          });
        }
        this.setParentAssociation$$.next(copyCrmObjects.copyCrmObjectsUpdated[0]);
      });
  }

  removeChildAssociation(cardData: ChildCompanyAssociationsCardData): void {
    this.confirmationModalService
      .openModal({
        type: 'warn',
        title: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.TITLE'),
        message: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.MESSAGE', {
          fromName: cardData.parentCompanyName,
          removeName: cardData.businessName,
        }),
        confirmButtonText: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.CONFIRM'),
        cancelButtonText: this.translateService.instant('ACTIONS.CANCEL'),
      })
      .pipe(
        take(1),
        switchMap((success) => {
          if (success) {
            return this.associationService.removeAssociation(cardData.associationId);
          }
          return of(null);
        }),
        tap((success) => {
          if (success) {
            this.removedAssociationIds$$.next([...this.removedAssociationIds$$.value, cardData.associationId]);
            this.preSelectedRows$$.next(
              this.preSelectedRows$$.getValue().filter((row) => row.associationId !== cardData.associationId),
            );
          }
        }),
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackService.openSuccessSnack('ASSOCIATIONS.REMOVE_DIALOG.SUCCESS', {
              duration: SNACKBAR_DURATION,
            });
          }
        },
        error: () => {
          this.snackService.openErrorSnack('ASSOCIATIONS.REMOVE_DIALOG.ERROR', {
            duration: SNACKBAR_DURATION,
          });
        },
      });
  }

  removeParentAssociation(cardData: ParentCompanyAssociationsCardData): void {
    this.confirmationModalService
      .openModal({
        type: 'warn',
        title: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.TITLE'),
        message: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.MESSAGE', {
          fromName: cardData.childCompanyName,
          removeName: cardData.businessName,
        }),
        confirmButtonText: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.CONFIRM'),
        cancelButtonText: this.translateService.instant('ACTIONS.CANCEL'),
      })
      .pipe(
        take(1),
        switchMap((success) => {
          if (success) {
            return this.associationService.removeAssociation(cardData.associationId);
          }
          return of(null);
        }),
        tap((success) => {
          if (success) {
            this.removedParentAssociationIds$$.next([
              ...this.removedParentAssociationIds$$.value,
              cardData.associationId,
            ]);
            this.setParentAssociation$$.next(undefined);
          }
        }),
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackService.openSuccessSnack('ASSOCIATIONS.REMOVE_DIALOG.SUCCESS', {
              duration: SNACKBAR_DURATION,
            });
          }
        },
        error: () => {
          this.snackService.openErrorSnack('ASSOCIATIONS.REMOVE_DIALOG.ERROR', {
            duration: SNACKBAR_DURATION,
          });
        },
      });
  }
}
